import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useQuery } from '@apollo/client';
import { Box, Button, CircularProgress, Stack, Typography, Unstable_Grid2 as Grid } from '@mui/material';
import { CompareArrows as CompareIcon, PhotoCamera as PhotoIcon } from '@mui/icons-material';
import ProgressCharts from 'components/progress/physical/ProgressCharts';
import ProgressCompareModal from 'components/progress/physical/ProgressCompareModal';
import ProgressEntryCard from 'components/progress/physical/ProgressEntryCard';
import { CLIENT_PHYSICAL_PROGRESS } from 'api/graphql/physicalProgress';
import { CLIENT_INTAKE } from 'api/graphql/nutrition';

const INTAKE_FIELDS = [
  { key: 'goal', label: 'Objetivo' },
  { key: 'weight', label: 'Peso', unit: ' kg' },
  { key: 'height', label: 'Estatura', unit: ' m' },
  { key: 'age', label: 'Edad' },
  { key: 'work_activity', label: 'Actividad laboral' },
  { key: 'rest', label: 'Descanso / sueño' },
  { key: 'food_habits', label: 'Hábitos alimenticios' },
  { key: 'preferences', label: 'Preferencias' },
  { key: 'cheat_meals', label: 'Comidas libres' },
  { key: 'supplements', label: 'Suplementos' },
  { key: 'dietary_restrictions', label: 'Restricciones / alergias' }
];

/**
 * Vista del ENTRENADOR del progreso físico de un cliente (solo si el cliente lo compartió):
 * datos de admisión (intake) + gráficas (evolución + donut) + entradas en solo lectura + comparar.
 * Las observaciones viven al fondo de la página (ProgressObservations), no aquí.
 */
function ClientProgressView({ clientId }) {
  const { data, loading } = useQuery(CLIENT_PHYSICAL_PROGRESS, { variables: { clientId: String(clientId) }, fetchPolicy: 'cache-and-network', skip: !clientId });
  const { data: intakeData } = useQuery(CLIENT_INTAKE, { variables: { user_id: String(clientId) }, fetchPolicy: 'cache-and-network', skip: !clientId });

  const [compareOpen, setCompareOpen] = useState(false);

  const entries = data?.clientPhysicalProgress ?? [];
  const intake = intakeData?.clientIntake;
  const canCompare = entries.length >= 2;
  const intakeFields = INTAKE_FIELDS.filter((f) => intake && intake[f.key] != null && intake[f.key] !== '');

  return (
    <Box sx={{ p: { xs: 1, sm: 2 } }}>
      {/* Datos de admisión (contexto para el entrenador) */}
      {intakeFields.length > 0 && (
        <Box sx={{ backgroundColor: 'surface.content', border: '1px solid', borderColor: 'hairline.main', borderRadius: '16px', p: { xs: 2, md: 2.5 }, mb: 2.5 }}>
          <Typography sx={{ fontWeight: 800, mb: 1.5 }}>Datos de admisión</Typography>
          <Grid container spacing={1.5}>
            {intakeFields.map((f) => (
              <Grid xs={6} sm={4} md={3} key={f.key}>
                <Box sx={{ backgroundColor: 'surface.raised', border: '1px solid', borderColor: 'hairline.main', borderRadius: '10px', p: 1.25, height: '100%' }}>
                  <Typography variant="caption" sx={{ color: 'text.secondary', textTransform: 'uppercase', fontWeight: 700, fontSize: '0.62rem', display: 'block' }}>{f.label}</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600, mt: 0.25 }}>{intake[f.key]}{f.unit || ''}</Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Box>
      )}

      {(() => {
        if (loading && entries.length === 0) {
          return <Box sx={{ textAlign: 'center', py: 6 }}><CircularProgress size={26} sx={{ color: 'secondary.main' }} /></Box>;
        }
        if (entries.length === 0) {
          return (
            <Box sx={{ textAlign: 'center', py: 6, color: 'text.secondary' }}>
              <PhotoIcon sx={{ fontSize: 44, color: 'text.disabled', mb: 1 }} />
              <Typography variant="body2">Este cliente no compartió su progreso físico contigo (o aún no tiene registros).</Typography>
            </Box>
          );
        }
        return (
          <>
            {canCompare && (
              <Stack direction="row" justifyContent="flex-end" sx={{ mb: 2 }}>
                <Button variant="outlined" color="inherit" startIcon={<CompareIcon />} onClick={() => setCompareOpen(true)} sx={{ textTransform: 'none', fontWeight: 700, color: 'text.primary', borderColor: 'hairline.strong' }}>
                  Comparar
                </Button>
              </Stack>
            )}
            <ProgressCharts entries={entries} />
            <Grid container spacing={2.5}>
              {entries.map((e) => (
                <Grid xs={12} md={6} key={e.id}>
                  <ProgressEntryCard entry={e} />
                </Grid>
              ))}
            </Grid>
          </>
        );
      })()}

      {compareOpen && <ProgressCompareModal open={compareOpen} onClose={() => setCompareOpen(false)} entries={entries} canNotify={false} />}
    </Box>
  );
}

ClientProgressView.propTypes = {
  clientId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired
};

export default ClientProgressView;
